import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Grid, MenuItem, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addCostCenter, editCostCenter, getCostCenterById } from '../../../api/endpoints/corporate';
import type { CorporatePolicy, CostCenter } from '../../../api/types/corporate';
import { FormDialog } from '../../../components/common';
import { useFreshEditRow } from '../../../components/useFreshEditRow';

const schema = z.object({
  costCenterName: z.string().min(1, 'Masraf merkezi adı gerekli'),
  defaultPolicyId: z.coerce.number().min(1, 'Politika seçimi gerekli'),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = { costCenterName: '', defaultPolicyId: 0 };

function toFormValues(cc: CostCenter): FormValues {
  return { costCenterName: cc.costCenterName ?? '', defaultPolicyId: cc.defaultPolicyId ?? 0 };
}

/** Create/edit dialog for Masraf Merkezi. `policies` powers the "Politika" select — the
 *  cost center's `policyName` is a read-only denormalized field, only `defaultPolicyId` is
 *  writable (confirmed via live swagger + curl against CostCenter/GetCostCenters). */
export function CostCenterFormDialog({
  open,
  costCenter,
  policies,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  costCenter: CostCenter | null;
  policies: CorporatePolicy[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!costCenter;

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyValues,
  });

  // Refetch the fresh row on edit-open; silently falls back to the list row on error
  // (this route can 200 with a business-logic null-ref for some accounts' data).
  const seed = useFreshEditRow({
    open,
    editing: costCenter,
    queryKey: ['corporate', 'costCenter', costCenter?.costCenterID],
    fetch: async (row) => (await getCostCenterById(row.costCenterID)).costCenter?.[0],
  });

  useEffect(() => {
    if (open) reset(seed ? toFormValues(seed) : emptyValues);
  }, [open, seed, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && costCenter
        ? editCostCenter({ ...values, costCenterID: costCenter.costCenterID })
        : addCostCenter(values),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Masraf merkezi güncellendi.' : 'Masraf merkezi oluşturuldu.', {
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['corporate', 'costCenters'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Masraf Merkezini Güncelle' : 'Yeni Masraf Merkezi'}
      onClose={onClose}
      onSubmit={submit}
      submitting={mutation.isPending}
      error={mutation.error}
    >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Masraf Merkezi Adı"
                error={!!errors.costCenterName}
                helperText={errors.costCenterName?.message}
                {...register('costCenterName')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                control={control}
                name="defaultPolicyId"
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    size="small"
                    label="Politika"
                    error={!!errors.defaultPolicyId}
                    helperText={errors.defaultPolicyId?.message}
                  >
                    <MenuItem value={0} disabled>
                      Seçiniz
                    </MenuItem>
                    {policies.map((p) => (
                      <MenuItem key={p.policyID} value={p.policyID}>
                        {p.policyName}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
          </Grid>
    </FormDialog>
  );
}
